import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { crmAuthedFetch } from './crmAuthedFetch';
import type { ProjectPLBucket } from '../types';

export const EXPENSE_LEGEND_DOC = 'settings/expenseLegend';
export const QUICKBOOKS_CHART_OF_ACCOUNTS_DOC = 'settings/quickbooksChartOfAccounts';

export interface LegendQbAccount {
  qbAccountId: string;
  qbAccountName?: string;
}

export interface ExpenseLegendRow {
  id: string;
  label: string;
  plBucket: ProjectPLBucket;
  /** Legacy single-account mapping — treated as the first group member. */
  qbAccountId?: string;
  qbAccountName?: string;
  /** Group members: QB chart-of-accounts entries cardholders may pick within this category. */
  qbAccounts?: LegendQbAccount[];
  sortOrder: number;
  active?: boolean;
}

/** All QB accounts in this group (merges legacy single mapping with the group list). */
export function legendQbAccountOptions(row: ExpenseLegendRow | null | undefined): LegendQbAccount[] {
  if (!row) return [];
  const out: LegendQbAccount[] = [];
  const seen = new Set<string>();
  const push = (acct: LegendQbAccount | undefined) => {
    if (!acct?.qbAccountId || seen.has(acct.qbAccountId)) return;
    seen.add(acct.qbAccountId);
    out.push(acct);
  };
  if (row.qbAccountId) push({ qbAccountId: row.qbAccountId, qbAccountName: row.qbAccountName });
  for (const acct of row.qbAccounts || []) push(acct);
  return out;
}

/** Short display label for a QB account (drops the parent path). */
export function qbAccountShortLabel(acct: LegendQbAccount): string {
  const name = acct.qbAccountName || acct.qbAccountId;
  const parts = name.split(':');
  return parts[parts.length - 1].trim() || name;
}

/** Count distinct QB accounts mapped across all legend groups. */
export function countLegendMappedAccounts(rows: ExpenseLegendRow[]): number {
  const seen = new Set<string>();
  for (const row of rows) {
    for (const acct of legendQbAccountOptions(row)) {
      seen.add(acct.qbAccountId);
    }
  }
  return seen.size;
}

/** Groups with no QuickBooks account mapped (write-back will skip). */
export function groupsMissingAccounts(rows: ExpenseLegendRow[]): ExpenseLegendRow[] {
  return rows.filter(r => legendQbAccountOptions(r).length === 0);
}

/** Map each QB account id to the first group label that contains it. */
export function legendAccountGroupLabels(
  rows: ExpenseLegendRow[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows) {
    for (const acct of legendQbAccountOptions(row)) {
      if (!map.has(acct.qbAccountId)) {
        map.set(acct.qbAccountId, row.label || row.id);
      }
    }
  }
  return map;
}

export interface QuickBooksAccountRow {
  qbId: string;
  name: string;
  fullyQualifiedName: string;
  accountType: string;
  accountSubType: string;
  classification: string;
  active: boolean;
}

export const DEFAULT_EXPENSE_LEGEND_ROWS: ExpenseLegendRow[] = [
  { id: 'design', label: 'Design', plBucket: 'design', sortOrder: 10, active: true },
  { id: 'materials', label: 'Materials', plBucket: 'timber', sortOrder: 20, active: true },
  { id: 'production', label: 'Production / shop', plBucket: 'production', sortOrder: 30, active: true },
  { id: 'freight', label: 'Freight', plBucket: 'freight', sortOrder: 40, active: true },
  { id: 'install', label: 'Install', plBucket: 'install', sortOrder: 50, active: true },
  { id: 'travel', label: 'Travel', plBucket: 'travel', sortOrder: 60, active: true },
  { id: 'overhead', label: 'Company overhead', plBucket: 'overhead', sortOrder: 70, active: true },
  { id: 'unassigned', label: 'Other / unassigned', plBucket: 'unassigned', sortOrder: 80, active: true },
];

export async function fetchExpenseLegend(): Promise<ExpenseLegendRow[]> {
  const snap = await getDoc(doc(db, EXPENSE_LEGEND_DOC));
  if (!snap.exists()) return DEFAULT_EXPENSE_LEGEND_ROWS.slice();
  const rows = (snap.data()?.rows || []) as ExpenseLegendRow[];
  return rows.length
    ? rows.filter(r => r.active !== false).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    : DEFAULT_EXPENSE_LEGEND_ROWS.slice();
}

export async function saveExpenseLegend(rows: ExpenseLegendRow[], uid?: string): Promise<void> {
  // Firestore rejects undefined values (e.g. a cleared legacy qbAccountId).
  const sanitized = JSON.parse(JSON.stringify(rows)) as ExpenseLegendRow[];
  await setDoc(
    doc(db, EXPENSE_LEGEND_DOC),
    {
      rows: sanitized,
      updatedAt: serverTimestamp(),
      ...(uid ? { updatedBy: uid } : {}),
    },
    { merge: true },
  );
}

export async function fetchQuickBooksChartOfAccounts(): Promise<QuickBooksAccountRow[]> {
  const snap = await getDoc(doc(db, QUICKBOOKS_CHART_OF_ACCOUNTS_DOC));
  if (!snap.exists()) return [];
  return (snap.data()?.accounts || []) as QuickBooksAccountRow[];
}

export async function fetchQuickBooksChartOfAccountsMeta(): Promise<{
  accounts: QuickBooksAccountRow[];
  syncedAt: Date | null;
}> {
  const snap = await getDoc(doc(db, QUICKBOOKS_CHART_OF_ACCOUNTS_DOC));
  if (!snap.exists()) return { accounts: [], syncedAt: null };
  const data = snap.data();
  const syncedAtRaw = data?.syncedAt as { toDate?: () => Date } | undefined;
  return {
    accounts: (data?.accounts || []) as QuickBooksAccountRow[],
    syncedAt: syncedAtRaw?.toDate?.() || null,
  };
}

export async function syncQuickBooksAccountsFromCloud(): Promise<{ count: number; accounts: QuickBooksAccountRow[] }> {
  const res = await crmAuthedFetch('https://us-central1-lctf-projects.cloudfunctions.net/syncQuickBooksAccounts', {
    method: 'POST',
    body: '{}',
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || `Sync failed (${res.status})`);
  }
  return { count: data.count, accounts: data.accounts || [] };
}
